import { Alert, Button, Input, Modal, Space, Table, Tabs, Tag, Tooltip, message } from 'antd'
import { SyncOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  apiCrossCheckFriendInvite,
  apiGetListZaloAccountFriends,
  apiGetListZaloAccounts,
  apiListPendingInvitationsByAccount,
  apiSyncZaloAccountFriends,
} from '../../api'

type Friend = {
  id: string
  friendZaloUid: string
  friendName: string
  friendAvatar: string
  friendPhone: string
  syncedAt: string
}

type Props = {
  open: boolean
  account: any
  canSync: boolean
  onClose: () => void
  onSynced?: () => void
}

const DEFAULT_PAGE_SIZE = 50
const PAGE_SIZE_OPTIONS = ['20', '50', '100', '200']

const SOURCE_LABEL: Record<string, { text: string; color: string }> = {
  MANUAL_REFRESH: { text: 'Đồng bộ thủ công', color: 'blue' },
  AUTO_SYNC: { text: 'Đồng bộ tự động', color: 'cyan' },
  INITIAL_LOGIN: { text: 'Đăng nhập lần đầu', color: 'green' },
  CRON: { text: 'Tự động (CRON)', color: 'geekblue' },
  QR_LOGIN: { text: 'Sau QR login', color: 'green' },
  IMPORT: { text: 'Nhập tay', color: 'purple' },
  INVITE_ACCEPTED: { text: 'Khách chấp nhận lời mời', color: 'cyan' },
}

// ─── Pending Invites Tab ───────────────────────────────────────────────────────

function PendingInvitesTable({ accountId, canManage }: { accountId: number; canManage: boolean }) {
  const [rows, setRows] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [loading, setLoading] = useState(false)
  const [crossCheck, setCrossCheck] = useState<Record<number, string>>({})
  const [externalUids, setExternalUids] = useState<any[]>([])
  const [syncing, setSyncing] = useState(false)

  const fetchPending = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    try {
      const r: any = await apiListPendingInvitationsByAccount({
        zaloAccountId: accountId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      })
      setRows(r?.data?.items ?? [])
      setTotal(r?.data?.total ?? 0)
    } finally {
      setLoading(false)
    }
  }, [accountId, page, pageSize])

  useEffect(() => {
    void fetchPending()
  }, [fetchPending])

  const handleCrossCheck = async () => {
    setSyncing(true)
    try {
      const r: any = await apiCrossCheckFriendInvite({ zaloAccountId: accountId })
      const map: Record<number, string> = {}
      ;(r?.data?.matches ?? []).forEach((m: any) => { map[m.invitationId] = '✓' })
      ;(r?.data?.orphans ?? []).forEach((m: any) => { map[m.invitationId] = '⚠️' })
      ;(r?.data?.notVerifiable ?? []).forEach((m: any) => { map[m.invitationId] = '🔶' })
      setCrossCheck(map)
      setExternalUids(r?.data?.external ?? [])
      message.success('Đồng bộ hoàn tất')
    } catch (e: any) {
      message.error('Đồng bộ thất bại: ' + (e?.message ?? 'unknown'))
    } finally {
      setSyncing(false)
    }
  }

  const columns = [
    { title: '', dataIndex: 'id', width: 40, render: (id: number) => crossCheck[id] ?? '' },
    { title: 'Khách', dataIndex: 'phoneCustomer' },
    { title: 'Đối tác', dataIndex: 'cpCode' },
    { title: 'Gửi lúc', dataIndex: 'sentAt' },
    { title: 'Người gửi', dataIndex: 'callerUserId' },
    { title: 'Zalo UID', dataIndex: 'targetZaloUid', render: (u: any) => (u ? String(u) : '—') },
  ]

  return (
    <>
      {canManage && (
        <Button onClick={handleCrossCheck} loading={syncing} icon={<SyncOutlined />} style={{ marginBottom: 12 }}>
          Đồng bộ Zalo
        </Button>
      )}
      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns as any}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onChange: (p, s) => {
            if (s !== pageSize) {
              setPage(1)
              setPageSize(s)
            } else {
              setPage(p)
            }
          },
        }}
      />
      {externalUids.length > 0 && (
        <Alert
          type="info"
          message={`🆕 ${externalUids.length} lời mời gửi ngoài hệ thống`}
          description={externalUids.map((u: any) => `${u.displayName} (${u.zaloUid})`).join(', ')}
          style={{ marginTop: 12 }}
        />
      )}
    </>
  )
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function FriendsModal({ open, account, canSync, onClose, onSynced }: Props) {
  const [friends, setFriends] = useState<Friend[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const accountId = Number(account?.id)

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  useEffect(() => {
    return clearPollTimer
  }, [])

  const fetchFriends = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    try {
      const res: any = await apiGetListZaloAccountFriends({
        zaloAccountId: accountId,
        search,
        page,
        size: pageSize,
      })
      const rows = Array.isArray(res?.data?.zaloAccountFriends) ? res.data.zaloAccountFriends : []
      setFriends(rows)
      setTotal(Number(res?.data?.total || 0))
    } catch (err: any) {
      message.error(err?.message || 'Tải danh sách bạn bè thất bại')
    } finally {
      setLoading(false)
    }
  }, [accountId, search, page, pageSize])

  useEffect(() => {
    if (open) {
      setPage(1)
      setPageSize(DEFAULT_PAGE_SIZE)
      setSearch('')
      void fetchFriends()
    }
  }, [open, accountId])

  useEffect(() => {
    if (open) void fetchFriends()
  }, [page, pageSize, search, open, fetchFriends])

  const pollUntilSynced = useCallback((baselineMs: number) => {
    const POLL_MS = 3000
    const TIMEOUT_MS = 90_000
    const startedAt = Date.now()
    const tick = async () => {
      if (Date.now() - startedAt > TIMEOUT_MS) {
        clearPollTimer()
        setSyncing(false)
        message.warning('Đồng bộ chậm hơn dự kiến — hãy kiểm tra lại sau ít phút.')
        return
      }
      try {
        const res: any = await apiGetListZaloAccounts({ ids: [accountId], page: 1, size: 1 })
        const row = Array.isArray(res?.data?.zaloAccounts) ? res.data.zaloAccounts[0] : null
        const ts = row?.friendsLastSyncedAt ? new Date(row.friendsLastSyncedAt).getTime() : 0
        if (Number.isFinite(ts) && ts > baselineMs) {
          clearPollTimer()
          setSyncing(false)
          message.success(`Đồng bộ ok: ${Number(row?.friendsCount || 0)} bạn`)
          await fetchFriends()
          onSynced?.()
          return
        }
      } catch {
        // transient — keep polling
      }
      pollTimerRef.current = setTimeout(tick, POLL_MS)
    }
    pollTimerRef.current = setTimeout(tick, POLL_MS)
  }, [accountId, fetchFriends, onSynced])

  const handleSync = async () => {
    if (!accountId) return
    clearPollTimer()
    setSyncing(true)
    const baselineMs = account?.friendsLastSyncedAt ? new Date(account.friendsLastSyncedAt).getTime() : 0
    try {
      const res: any = await apiSyncZaloAccountFriends({ zaloAccountId: accountId })
      const data = res?.data || {}
      if (typeof data.friendsCount === 'number' && data.friendsLastSyncedAt) {
        message.success(`Đồng bộ ok: ${data.friendsCount} bạn (+${data.inserted || 0} mới, -${data.removed || 0} unfriend)`)
        await fetchFriends()
        onSynced?.()
        setSyncing(false)
        return
      }
      message.info('Đã gửi yêu cầu đồng bộ — đang chạy nền, kết quả sẽ cập nhật trong vài chục giây.')
      pollUntilSynced(Number.isFinite(baselineMs) ? baselineMs : 0)
    } catch (err: any) {
      setSyncing(false)
      message.error(err?.response?.data?.message || err?.message || 'Đồng bộ thất bại')
    }
  }

  const friendColumns = [
    {
      title: 'Tên',
      dataIndex: 'friendName',
      render: (v: string) => v || '(no name)',
    },
    {
      title: 'UID',
      dataIndex: 'friendZaloUid',
      render: (v: string) => {
        if (!v) return '—'
        const tail = v.length > 8 ? `…${v.slice(-6)}` : v
        return (
          <Tooltip title={v}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{tail}</span>
          </Tooltip>
        )
      },
    },
    {
      title: 'SĐT',
      dataIndex: 'friendPhone',
      render: (v: string) => (v ? v.replace(/^\+84/, '0') : '—'),
    },
    {
      title: 'Nguồn',
      dataIndex: 'source',
      render: (v: string) => {
        if (!v) return '—'
        const meta = SOURCE_LABEL[v]
        return meta ? <Tag color={meta.color}>{meta.text}</Tag> : <Tag>{v}</Tag>
      },
    },
  ]

  const friendsTab = (
    <>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Input.Search
          placeholder="Tìm theo tên / UID / SĐT..."
          allowClear
          style={{ width: 360 }}
          onSearch={(v) => { setPage(1); setSearch(v) }}
        />
        {canSync && (
          <Button
            type="primary"
            icon={<SyncOutlined />}
            loading={syncing}
            onClick={handleSync}
          >
            Đồng bộ ngay
          </Button>
        )}
      </Space>
      <Table
        columns={friendColumns as any}
        dataSource={friends}
        loading={loading}
        rowKey="id"
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onChange: (p, ps) => {
            if (ps !== pageSize) {
              setPage(1)
              setPageSize(ps)
            } else {
              setPage(p)
            }
          },
          showTotal: (t) => `Tổng ${t} bạn`,
        }}
      />
    </>
  )

  return (
    <Modal
      title={`Bạn bè của ${account?.displayName || ''} (#${accountId})`}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Đóng</Button>}
      width={900}
    >
      <Tabs
        defaultActiveKey="friends"
        items={[
          { key: 'friends', label: 'Bạn bè', children: friendsTab },
          {
            key: 'pending',
            label: 'Lời mời đã gửi',
            children: <PendingInvitesTable accountId={accountId} canManage={canSync} />,
          },
        ]}
      />
    </Modal>
  )
}
